import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  customLabels,
  fieldMapping,
} from "../../data/buildingPermit/fieldDatas";
import { setBuildingState } from "../../store/actions/permitFormActions/buildingStateActions";
import { formikRef } from "../../store/formikStore/formikStore";
import { pushNewDesignPro } from "../../store/actions/userActions";

const DesignProAddInfoModal = () => {
  const formik = formikRef.current;
  const dispatch = useDispatch();

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const { addingNewDesignPro } = useSelector((state) => state.buildingState);

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const currUserEmail =
    currentSession === "userSession"
      ? currentUser?.email
      : currentUser?.subuser?.email;
  const currUserName =
    `${currentUser?.user?.personal?.[0]?.firstName} ${currentUser?.user?.personal?.[0]?.lastName}` ||
    "";

  const [fieldValues, setFieldValues] = useState({
    design_pro_company: "",
    design_pro_license_name: "",
    design_pro_license_num: "",
    design_pro_address: "",
    design_pro_phone: "",
    design_pro_fax: "",
    design_pro_email: "",
  });

  useEffect(() => {
    const callFunc = () => {
      let error = false;

      Object.keys(fieldMapping).forEach((item) => {
        if (item !== "design_pro_fax") {
          const val = fieldValues[item];

          if (item === "design_pro_phone" || item === "design_pro_fax") {
            if (val === "" || val.length < 12) {
              error = true;
            }
          } else {
            if (val === "" || val.length < 3) {
              error = true;
            }
          }
        }
      });

      setSubmitDisabled(error);
    };

    callFunc();
  }, [fieldValues]);

  const runFieldCange = (event) => {
    const { name, value } = event.target;
    setFieldValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddNew = async () => {
    try {
      await dispatch(setBuildingState({ designProLoading: true }));

      const newID = Date.now().toString();
      const createdAt = Math.floor(Date.now() / 1000);

      const savedProjectObj = {
        id: newID,
        companyName: fieldValues.design_pro_company,
        licenseName: fieldValues.design_pro_license_name,
        licenseNumber: fieldValues.design_pro_license_num,
        address: fieldValues.design_pro_address,
        phone: fieldValues.design_pro_phone,
        fax: fieldValues.design_pro_fax,
        email: fieldValues.design_pro_email,
        created_by_email: currUserEmail,
        created_by_name: currUserName,
        is_subuser: currentSession !== "userSession",
        created_at: createdAt,
      };

      await dispatch(pushNewDesignPro(savedProjectObj));
      // await dispatch(handleSaveProjects());

      formik?.setValues((prevValues) => ({
        ...prevValues,
        ...fieldValues,
        designProfessional: newID,
      }));

      await dispatch(setBuildingState({ addingNewDesignPro: false }));
    } catch (e) {
    } finally {
      await dispatch(setBuildingState({ designProLoading: false }));
    }
  };

  const cleanPhone = (e) => {
    const { name, value } = e.target;

    let digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters

    // If the number of digits is greater than 10, slice to 10 digits
    if (digitsOnly?.length > 10) {
      digitsOnly = digitsOnly.slice(0, 10);
    }

    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    setFieldValues((prev) => ({
      ...prev,
      [name]: formattedPhoneNumber,
    }));
  };

  return (
    <Modal
      open={addingNewDesignPro}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "transparent", // Add a semi-transparent backdrop
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%", // Adjust width for smaller screens
          maxWidth: 600, // Set a maximum width
          bgcolor: "background.paper",
          boxShadow: "2px 10px 10px 5px rgba(0, 0, 0, 0.5)",
          borderRadius: "8px",
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h5" align="center" color="primary">
            Add New Design Professional
          </Typography>
        </Box>

        {/* Scrollable Content Section */}
        <Box
          sx={{
            maxHeight: "60vh", // Limit height to 60% of the viewport
            overflowY: "auto", // Enable scrolling when content overflows
            p: 2,
          }}
        >
          <>
            {Object.keys(fieldMapping).map((key, i) => (
              <TextField
                autoComplete="off"
                key={key + i}
                fullWidth
                onKeyUp={(e) => {
                  if (key === "design_pro_phone" || key === "design_pro_fax") {
                    cleanPhone(e);
                  }
                }}
                margin="normal"
                variant="filled"
                label={customLabels[key]}
                name={key}
                value={fieldValues[key]}
                onChange={runFieldCange}
                onBlur={(event) => {
                  formik?.handleBlur(event);
                  formik?.setFieldTouched(key, true, true);
                }}
                error={formik?.touched[key] && Boolean(formik?.errors[key])}
                helperText={formik?.touched[key] && formik?.errors[key]}
              />
            ))}
          </>
        </Box>

        {/* Footer Section with Buttons */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            fullWidth
            onClick={() => {
              dispatch(setBuildingState({ addingNewDesignPro: false }));
            }}
            color="error"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            disabled={submitDisabled}
            fullWidth
            onClick={handleAddNew}
            color="success"
            variant="contained"
          >
            Add
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(DesignProAddInfoModal);
